<template>
  <div id="app" class="h-100" :class="[skinClasses]">
    <vue-element-loading :active="appLoading" spinner="bar-fade-scale" />

    <div v-if="!appLoading" class="h-100">
      <component :is="layout">
        <router-view />
      </component>
    </div>
    <div v-else class="initial-app-loading-message"></div>
  </div>
</template>

<!-- eslint-disable vue/order-in-components -->
<script>
// This will be populated in `beforeCreate` hook
import { $themeColors, $themeBreakpoints, $themeConfig } from "@themeConfig";
import { provideToast } from "vue-toastification/composition";
import { watch } from "@vue/composition-api";
import useAppConfig from "@core/app-config/useAppConfig";
import VueElementLoading from "vue-element-loading";

import { useWindowSize, useCssVar } from "@vueuse/core";
import SessionTimeout from "@/views/auth/SessionTimeout.vue";
import store from "@/store";

const LayoutVertical = () => import("@/layouts/vertical/LayoutVertical.vue");
const LayoutHorizontal = () => import("@/layouts/horizontal/LayoutHorizontal.vue");
const LayoutFull = () => import("@/layouts/full/LayoutFull.vue");
import { GET_TOKEN } from "@/services/local.storage.service";
import { mapGetters, mapActions } from "vuex";

export default {
  components: {
    // Layouts
    LayoutHorizontal,
    LayoutVertical,
    LayoutFull,
    SessionTimeout,
    VueElementLoading
  },
  data() {
    return {
      appLoading: true // Set to true initially to show the loader
    };
  },
  metaInfo: {
    // if no subcomponents specify a metaInfo.title, this title will be used
    title: "Midas",
    // all titles will be injected into this template
    titleTemplate: "%s | Midas App"
  },
  // ! We can move this computed: layout & contentLayoutType once we get to use Vue 3
  // Currently, router.currentRoute is not reactive and doesn't trigger any change
  computed: {
    layout() {
      if (this.$route.meta.layout === "full") return "layout-full";
      return `layout-${this.contentLayoutType}`;
    },
    contentLayoutType() {
      return this.$store.state.appConfig.layout.type;
    }
  },
  beforeCreate() {
    // Set colors in theme
    const colors = ["primary", "secondary", "success", "info", "warning", "danger", "light", "dark"];
    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = colors.length; i < len; i++) {
      $themeColors[colors[i]] = useCssVar(`--${colors[i]}`, document.documentElement).value.trim();
    }

    // Set Theme Breakpoints
    const breakpoints = ["xs", "sm", "md", "lg", "xl"];

    // eslint-disable-next-line no-plusplus
    for (let i = 0, len = breakpoints.length; i < len; i++) {
      $themeBreakpoints[breakpoints[i]] = Number(useCssVar(`--breakpoint-${breakpoints[i]}`, document.documentElement).value.slice(0, -2));
    }

    // Set RTL
    const { isRTL } = $themeConfig.layout;
    document.documentElement.setAttribute("dir", isRTL ? "rtl" : "ltr");
  },
  async mounted() {
    // Wait for config to be loaded before checking session
    await this.waitForConfig();
    await this.checkSession();
    this.appLoading = false;
  },
  setup() {
    const { skin, skinClasses } = useAppConfig();

    // If skin is dark when initialized => Add class to body
    if (skin.value === "dark") document.body.classList.add("dark-layout");

    // Provide toast for Composition API usage
    // This for those apps/components which uses composition API
    // Demos will still use Options API for ease
    provideToast({
      hideProgressBar: true,
      closeOnClick: false,
      closeButton: false,
      icon: false,
      timeout: 3000,
      transition: "Vue-Toastification__fade"
    });

    // Set Window Width in store
    store.commit("app/UPDATE_WINDOW_WIDTH", window.innerWidth);
    const { width: windowWidth } = useWindowSize();
    watch(windowWidth, val => {
      store.commit("app/UPDATE_WINDOW_WIDTH", val);
    });

    return {
      skinClasses
    };
  },
  async created() {
    if (this.$workbox) {
      this.$workbox.addEventListener("waiting", () => {
        this.showUpgradeUI = true;
      });
    }
  },
  methods: {
    async accept() {
      this.showUpgradeUI = false;
      await this.$workbox.messageSW({ type: "SKIP_WAITING" });
    },
    async waitForConfig() {
      // Wait for config URLs to be loaded from main.js
      const maxAttempts = 50; // 5 seconds max wait
      let attempts = 0;

      while (!store.state.config.authUrl && attempts < maxAttempts) {
        await new Promise(resolve => setTimeout(resolve, 100));
        attempts++;
      }

      if (!store.state.config.authUrl) {
        console.error("Config failed to load");
        throw new Error("Failed to load application configuration");
      }
    },
    async checkSession() {
      try {
        if (GET_TOKEN()) {
          await store.dispatch("auth/GET_USER");
        }
      } catch (error) {
        this.showToast(`error`, error);
      }
    }
  }
};
</script>
